import { SvgComponent } from "@/components/base/Svg"
import ButtonRegular from "@/components/composite/ButtonRegular/main"
import { useDepositSubmit, usePaymentMethods } from "@/features/business"
import { useModalSlideUp } from "@/features/useModal"
import { useSearchAction } from "@/features/useSearchAction"
import Wallet from "@/features_signal/wallet"
import { getAssetsUrlWithHttp, resolveAssetImage } from "@/utils/common"
import styled from "@emotion/styled"
import { useCallback, useEffect, useId, useState } from "react"
import { toast } from "react-toastify"
import DepositBlockTitle from "../components/DepositBlockTitle"
import LaVoucher from "../components/LazadaVoucher"
import Promotions from "../components/Promotions"
import VoucherCode from "../components/VoucherCode"
import IntInput, { formatThousandsIntl } from "../components/IntInput"
import AppSignals from "@/features_signal/app"

const DepositContainer = styled.div`
    font-family: Gilroy;
    position: relative;
    background-color: rgba(242, 243, 245, 1);
`

const DepositContent = styled.div`
    max-height: 70vh;
    overflow-y: auto;
    padding-bottom: 80rem;
`

const DepositTitle = styled.div`
    display: flex;
    justify-content: center;
    font-weight: 500;
    font-size: 18rem;
    padding-top: 8rem;
    height: 72rem;
    text-align: center;
    color: rgba(255, 255, 255, 0.95);
    margin-top: -12rem;
    position: relative;
    & > img {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
    }
    & > span {
        position: relative;
    }
`

const DepositTitleImg = styled.img`
    object-fit: cover;
`

const DepositTitleTxt = styled.span`
    color: rgba(255, 255, 255, 0.95);
    text-align: center;
    text-shadow: 0 2rem 4rem rgba(110, 12, 29, 0.24);
    font-size: 18rem;
    font-style: normal;
    font-weight: 500;
    line-height: 28rem;
`
const PaymentContainer = styled.div`
    padding: 12rem;
    background-color: #ffffff;
    border-radius: 12rem;
    margin-left: 12rem;
    margin-right: 12rem;
    position: relative;
`

const ChannelContainer = styled.div`
    margin-top: 12rem;
    display: flex;
    gap: 2rem;
    overflow-x: auto;
    &::-webkit-scrollbar {
        height: 0;
        background: transparent;
        opacity: 0;
    }
`

const PaymentMethod = styled.div<{
    selected: boolean
}>`
    flex-shrink: 0;
    width: 80rem;
    height: 72rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6rem;
    border-width: 1rem;
    border-style: solid;
    border-color: ${(props) => (props.selected ? "#054BF0" : "transparent")};
    color: ${(props) => (props.selected ? "#054BF0" : "#69728C")};
    width: 80rem;
    height: 72rem;
    user-select: none;
    cursor: pointer;
`

const PaymentText = styled.div`
    font-weight: 400;
    text-align: center;
    vertical-align: middle;
    height: 16rem;
    line-height: 16rem;
    margin-top: 4rem;
`

const BottomBlock = styled.div`
    padding: 12rem;
    background-color: #ffffff;
    width: 100%;
    display: flex;
    justify-content: center;
`

const SubmitButton = styled(ButtonRegular)`
    background: ${({ disabled }) =>
        disabled
            ? "linear-gradient(90deg, #FFADC2 10%, #FFADEB 100%)"
            : "linear-gradient(90deg, #FF3568 10%, #FF33CC 100%)"};
    width: 343rem;
    height: 40rem;
    line-height: 40rem;
    font-weight: 500;
    font-size: 16rem;
    text-align: center;
    color: ${({ disabled }) => (disabled ? "rgba(255, 255, 255, 1)" : "#ffffff")};
    box-shadow: none;
`

const OptionClose = styled(SvgComponent("pages/UserCenterPage/deposit/close.svg"))`
    width: 24rem;
    height: 24rem;
    position: absolute;
    top: 16rem;
    right: 16rem;
    cursor: pointer;
`

const PaymentIcon = styled.img`
    width: 36rem;
    height: 36rem;
    object-fit: cover;
    border-radius: 4rem;
`

export default function Deposit() {
    const { channels, selectedChannel, laCodeList, setSelectedPaymentMethod } = usePaymentMethods()

    const handleClose = () => {
        close()
        clearAction()
    }

    const isLa = selectedChannel?.id.toString() === "1008" //id=1008，判断为lazada兑换支付
    const [laCode, setLaCode] = useState<string>("")
    const [laCodeErrMessage, setLaCodeErrMessage] = useState<string>("")
    const [amountErrMessage, setAmountErrMessage] = useState<string>("")
    const [submitEnable, setSubmitEnable] = useState(true)

    const { handleWebDeposit, handleLaDeposit } = useDepositSubmit({
        onSuccess: () => {
            handleClose()
            if (isLa) {
                setTimeout(() => {
                    Wallet.fetchWallet()
                }, 1000)
            }
            setSubmitEnable(true)
        },
        onError: (_errMsg: string) => {
            setSubmitEnable(true)
            if (isLa) {
                setLaCodeErrMessage(_errMsg)
            } else {
                setAmountErrMessage(_errMsg)
            }
        },
    })

    const { close } = useModalSlideUp("search-action")
    const { clearAction } = useSearchAction()

    useEffect(() => {
        setSubmitEnable(true)
    }, [selectedChannel])

    const [amount, setAmount] = useState<string>("")

    const validateAmount = useCallback(() => {
        if (!amount || !selectedChannel) {
            return false
        }
        return (
            parseFloat(amount.toString()) >= parseFloat(selectedChannel.min_amount) &&
            parseFloat(amount.toString()) <= parseFloat(selectedChannel.max_amount)
        )
    }, [amount, selectedChannel])

    useEffect(() => {
        if (isLa && laCode.length <= 5 && laCode.length > 0) {
            setLaCodeErrMessage("Verification code error")
        } else {
            setLaCodeErrMessage("")
        }
    }, [isLa, laCode])

    useEffect(() => {
        if (!selectedChannel || !amount) {
            return setAmountErrMessage("")
        }
        const { min_amount, max_amount } = selectedChannel
        if (min_amount && max_amount) {
            setAmountErrMessage(
                parseFloat(amount) < parseFloat(min_amount) ||
                    parseFloat(amount) > parseFloat(max_amount)
                    ? `Enter Amount: ${formatThousandsIntl(min_amount)}-${formatThousandsIntl(max_amount)}`
                    : "",
            )
        }
    }, [selectedChannel, amount])

    useEffect(() => {
        setAmount("")
        setAmountErrMessage("")
    }, [selectedChannel])

    const isFormValid = () => {
        if (isLa) {
            return laCode.length >= 5 && submitEnable
        } else {
            return !!selectedChannel && validateAmount() && submitEnable
        }
    }

    const handleSubmit = async () => {
        if (isLa && laCode) {
            setSubmitEnable(false)
            await handleLaDeposit({
                exchangeCode: laCode,
            })
            return
        }
        if (selectedChannel) {
            setSubmitEnable(false)
            await handleWebDeposit({
                amount: parseFloat(amount),
                pid: selectedChannel.id,
                channelName: selectedChannel.name,
            })
        } else {
            toast.error("payment method not found")
        }
        setTimeout(() => {
            setSubmitEnable(true)
        }, 3000)
    }

    const sKey = useId()

    return (
        <DepositContainer>
            <DepositTitle>
                <DepositTitleImg
                    src={resolveAssetImage("pages/UserCenterPage/deposit/deposit_head.webp")}
                    alt=""
                />
                <DepositTitleTxt>Deposit</DepositTitleTxt>
            </DepositTitle>
            <DepositContent>
                {AppSignals.isMayaminiUser() ? (
                    ""
                ) : (
                    <PaymentContainer>
                        <DepositBlockTitle>Payment Methods</DepositBlockTitle>
                        <ChannelContainer>
                            {channels.map((channel, j) => {
                                return (
                                    <PaymentMethod
                                        key={`${sKey}-${channel.name}-${j}`}
                                        selected={selectedChannel?.id === channel.id}
                                        onClick={() => {
                                            setSelectedPaymentMethod(channel)
                                        }}
                                    >
                                        <PaymentIcon
                                            src={getAssetsUrlWithHttp(channel?.img)}
                                            alt=""
                                        />
                                        <PaymentText>{channel?.name}</PaymentText>
                                    </PaymentMethod>
                                )
                            })}
                        </ChannelContainer>
                    </PaymentContainer>
                )}
                {isLa ? (
                    <VoucherCode
                        code={laCode}
                        setCode={setLaCode}
                        errorMsg={laCodeErrMessage}
                    />
                ) : (
                    <IntInput
                        amount={amount}
                        setAmount={setAmount}
                        min={selectedChannel?.min_amount || "0"}
                        max={selectedChannel?.max_amount || "0"}
                        amountLimit={selectedChannel?.amount_limit}
                        errorMsg={amountErrMessage}
                        pagcorTip={selectedChannel?.tips}
                    />
                )}
                {AppSignals.isMayaminiUser() ? "" : <Promotions isLa={isLa} />}
                {isLa ? <LaVoucher list={laCodeList} /> : null}
                <OptionClose onClick={handleClose} />
            </DepositContent>
            <BottomBlock>
                <SubmitButton
                    disabled={!isFormValid()}
                    onClick={() => {
                        if (!isFormValid()) return toast.error("amount error")
                        handleSubmit()
                    }}
                >
                    Deposit now
                </SubmitButton>
            </BottomBlock>
        </DepositContainer>
    )
}
